/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { ReactNode, useMemo, type FC } from 'react'
import { Button, Col, Form, Row, Stack } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  question: yup.string().required('A pergunta é obrigatória').default('').min(0).max(255),
  answer: yup.string().required('O resposta é obrigatório').default('').min(0).max(5000),
  status: yup.string().oneOf(['active', 'inactive', 'deleted', 'draft']).default('active')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormFrequentlyAskedQuestionHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormFrequentlyAskedQuestionHandleSubmit,
  initialValues?: ValidationSchema,
  isLoading?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormFrequentlyAskedQuestion is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormFrequentlyAskedQuestion from './src/views/PostEdit/FormFrequentlyAskedQuestion.tsx'
 *
 * const MyComponent = () => (
 *   <FormFrequentlyAskedQuestion />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormFrequentlyAskedQuestion: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Pergunta</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o título da pergunta'
            name='question'
            value={formik.values.question}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Resposta</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder='Informe a resposta da pergunta'
            name='answer'
            value={formik.values.answer}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Situação</Form.Label>
          <Form.Select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Col md={12}>
          <Stack direction="horizontal" gap={3}>
            <div className="p-2 ms-auto">
              <Button
                size='sm'
                variant="outline-link"
                style={{ marginRight: 10 }} onClick={() => navigate('/frequently-asked-question')}
                disabled={formik.isSubmitting || isLoading}
              >
                Voltar
              </Button>
            </div>
            <div className="p-2">
              <Button
                size='sm'
                variant="outline-primary"
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
              >
                Salvar
              </Button>
            </div>
            <div className="p-2">
              {customFooterMessage}
            </div>
          </Stack>
        </Col>
      </Row>
    </Form>
  )
}

export default FormFrequentlyAskedQuestion
